
 import React, {
  useState,
  useContext,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Form,
  Button,
  Spinner,
  Alert,
  ProgressBar,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import zxcvbn from 'zxcvbn';
import axios, { setLoginSource } from '../axiosAlertConfig';
import { UserContext } from '../../userAlertContext';
import QRImg from './ialertTESTu_qr.png';
import styles from './Login.module.css';
import IALERTTRANSImg from './INCIDENTALERTLIGHTTRANS.png';
import {
  clearMfaReturnTo,
  getIncidentLoginTarget,
  rememberMfaReturnTo,
} from '../../utils/incidentDeepLink';

const RESET_RULES = [
  { re: /.{12,}/, label: '≥ 12 characters' },
  { re: /[A-Z]/, label: 'upper-case letter' },
  { re: /[a-z]/, label: 'lower-case letter' },
  { re: /[0-9]/, label: 'number' },
  { re: /[^A-Za-z0-9]/, label: 'symbol' },
];

const scoreVariant = ['danger', 'danger', 'warning', 'info', 'success'];
const MFA_LINK_CHALLENGE_KEY = 'incidentAlertOneTapMfaChallengeID';
const MFA_LINK_DELIVERY_KEY = 'incidentAlertOneTapMfaDeliveryMethods';

const passesAllRules = (pwd) => RESET_RULES.every((rule) => rule.re.test(pwd));

const getEmptyResetForm = () => ({
  username: '',
  resetCode: '',
  newPassword: '',
  confirmPassword: '',
});

const PASSWORD_INPUT_STYLE = {
  minWidth: 0,
  borderTopRightRadius: 0,
  borderBottomRightRadius: 0,
};

 

const PASSWORD_TOGGLE_STYLE = {
  width: 40,
  minWidth: 40,
  padding: 0,
  borderTopLeftRadius: 0,
  borderBottomLeftRadius: 0,
  borderLeft: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};



 function PasswordControl({
  id,
  name,
  autoComplete,
  placeholder,
  value,
  onChange,
  visible,
  onToggle,
}) {
  const action = visible ? 'Hide' : 'Show';
  const passwordLabel = placeholder.toLowerCase();

  return (
    <div className="d-flex w-100">
      <Form.Control
        id={id}
        name={name}
        size="sm"
        type={visible ? 'text' : 'password'}
        autoComplete={autoComplete}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        placeholder={placeholder}
        aria-label={placeholder}
        value={value}
        onChange={onChange}
        className="text-center incident-alert-password-input"
        style={PASSWORD_INPUT_STYLE}
      />

      <Button
        type="button"
        variant="outline-secondary"
        size="sm"
        onClick={onToggle}
        aria-label={`${action} ${passwordLabel}`}
        aria-controls={id}
        aria-pressed={visible}
        title={`${action} ${passwordLabel}`}
        style={PASSWORD_TOGGLE_STYLE}
      >
        {visible ? (
          // Eye with slash = password is currently visible
          <svg
            width="18"
            height="18"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M1 8s2.5-4.5 7-4.5S15 8 15 8s-2.5 4.5-7 4.5S1 8 1 8Z" />
            <circle cx="8" cy="8" r="2.25" />
            <line x1="2" y1="2" x2="14" y2="14" />
          </svg>
        ) : (
          // Eye = click to show password
          <svg
            width="18"
            height="18"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M1 8s2.5-4.5 7-4.5S15 8 15 8s-2.5 4.5-7 4.5S1 8 1 8Z" />
            <circle cx="8" cy="8" r="2.25" />
          </svg>
        )}
      </Button>
    </div>
  );
}



export default function Login() {
  const {
    setUser,
    setIsLoading,
    setSessionId,
    setIAccountID,
    setIBuildingID,
    setBuildingName,
  } = useContext(UserContext);

  const navigate = useNavigate();
  const location = useLocation();
  const incidentTarget = useMemo(
    () => getIncidentLoginTarget(location),
    [location]
  );

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [infoMsg, setInfoMsg] = useState('');

  const [resetStep, setResetStep] = useState(0); // 0=login, 1=send code, 2=update password
  const [resetForm, setResetForm] = useState(getEmptyResetForm);

  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installAvailable, setInstallAvailable] = useState(false);

  const [showQr, setShowQr] = useState(false);

  const clearedOnceRef = useRef(false);

  const { score } = useMemo(
    () => zxcvbn(resetForm.newPassword || ''),
    [resetForm.newPassword]
  );
  const strongPassword =
    passesAllRules(resetForm.newPassword) && score >= 3;
  const passwordStrengthPercent = Math.min((score + 1) * 20, 100);

  useEffect(() => {
    if (clearedOnceRef.current) return;
    clearedOnceRef.current = true;

    try {
      sessionStorage.clear();
      localStorage.removeItem('isTestIncidentOverride');
      localStorage.removeItem('iBuildingID');
      localStorage.removeItem('buildingName');
    } catch {}

    setUser?.(null);
    setSessionId?.(null);
    setIAccountID?.('');
    setIBuildingID?.('');
    setBuildingName?.('');
  }, []);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setInstallAvailable(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') setInstallAvailable(false);
    setDeferredPrompt(null);
  };

  const routeToActiveOrPanel = async (acctId, bldgId) => {
    if (!acctId || !bldgId) return navigate('/reportincidentpanel');

    try {
      const { data: active } = await axios.get(
        '/ialert/incidents/active-latest',
        {
          params: { iAccountID: acctId, iBuildingID: bldgId, hours: 24 },
        }
      );

      if (active?.found && active?.incident?.incidentID) {
        navigate(`/activeincidentpage/${active.incident.incidentID}`, {
          state: { incident: active.incident },
        });
      } else {
        navigate('/reportincidentpanel');
      }
    } catch {
      navigate('/reportincidentpanel');
    }
  };

  const resetResetFlow = () => {
    setResetStep(0);
    setResetForm(getEmptyResetForm());
    setShowLoginPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  const handleStartReset = () => {
    setErrorMsg('');
    setInfoMsg('');
    setShowLoginPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setResetForm({
      ...getEmptyResetForm(),
      username: username.trim(),
    });
    setResetStep(1);
  };

  const handleCancelReset = () => {
    setErrorMsg('');
    setInfoMsg('');
    resetResetFlow();
  };

  const handleSendResetCode = async (e) => {
    e.preventDefault();

    if (!resetForm.username.trim()) {
      setErrorMsg('Enter your username.');
      setInfoMsg('');
      return;
    }

    try {
      setBusy(true);
      setErrorMsg('');
      setInfoMsg('');

      const { data } = await axios.post('/LoginNew/newAuth/forgotPassword', {
        username: resetForm.username.trim(),
      });

      setInfoMsg(data.message || 'Reset code sent.');
      setResetStep(2);
    } catch (err) {
      setErrorMsg(
        err.response?.data?.message || 'Unable to send reset code.'
      );
    } finally {
      setBusy(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (!resetForm.resetCode.trim()) {
      setErrorMsg('Enter the reset code.');
      setInfoMsg('');
      return;
    }

    if (!strongPassword) {
      setErrorMsg('Choose a stronger password.');
      setInfoMsg('');
      return;
    }

    if (resetForm.newPassword !== resetForm.confirmPassword) {
      setErrorMsg('Passwords do not match.');
      setInfoMsg('');
      return;
    }

    try {
      setBusy(true);
      setErrorMsg('');
      setInfoMsg('');

      const resetUsername = resetForm.username.trim();

      const { data } = await axios.post(
        '/LoginNew/newAuth/resetPasswordWithCode',
        {
          resetCode: resetForm.resetCode.trim(),
          newPassword: resetForm.newPassword,
        }
      );

      setUsername(resetUsername);
      setPassword('');
      resetResetFlow();
      setInfoMsg(
        data.message || 'Password reset. Please log in with your new password.'
      );
    } catch (err) {
      setErrorMsg(
        err.response?.data?.message || 'Unable to reset password.'
      );
    } finally {
      setBusy(false);
    }
  };

  const handleLogin = async () => {
    try {
      setBusy(true);
      setIsLoading?.(true);
      setErrorMsg('');
      setInfoMsg('');

      const requestedIncidentID = String(
        incidentTarget.incidentID || ''
      ).trim();

      const { data } = await axios.post('/LoginNew/loginnew', {
        username,
        password,
        clientApp: 'IncidentAlert',
        incidentID: requestedIncidentID,
        mfaReturnTo: incidentTarget.returnTo || '/reportincidentpanel',
      });

      if (!data.success) {
        setBusy(false);
        setIsLoading?.(false);
        return setErrorMsg(data.message || 'Invalid credentials');
      }

      const linkedIncidentID = String(
        requestedIncidentID || data.incidentID || ''
      ).trim();

      const sid = data.session_id;
      sessionStorage.setItem('sessionId', sid);
      sessionStorage.setItem('sessionCreationTime', new Date().toISOString());
      sessionStorage.setItem('loginSource', 'iAlert');
      sessionStorage.setItem('iAccountID', String(data.iAccountID || ''));
      setLoginSource('iAlert');
      setSessionId?.(sid);

      window.dispatchEvent(
        new CustomEvent('newSessionId', {
          detail: { sessionId: sid, source: 'iAlert' },
        })
      );

      await axios.get('/authenticateSession', {
        headers: { sessionid: sid },
      });

      const campusIDs = Array.isArray(data.campusIDs) ? data.campusIDs.map(String) : [];
      const campusNames = Array.isArray(data.campusNames) ? data.campusNames.map(String) : [];

      let targetIncident = null;
      let incidentLoadError = null;
      let targetPath = linkedIncidentID
        ? `/activeincidentpage/${encodeURIComponent(linkedIncidentID)}`
        : (incidentTarget.returnTo || '/reportincidentpanel');

      if (linkedIncidentID) {
        try {
          const { data: incidentData } = await axios.get(
            `/ialert/incidents/${encodeURIComponent(linkedIncidentID)}`,
            { withCredentials: true }
          );

          const loadedIncident = Array.isArray(incidentData) ? incidentData[0] : incidentData;

          if (loadedIncident?.incidentID) {
            targetIncident = loadedIncident;
            targetPath = `/activeincidentpage/${targetIncident.incidentID}`;

            const incidentBuildingID = String(
              targetIncident.iBuildingID || targetIncident.iCampusID || ''
            ).trim();
            const incidentBuildingName = String(targetIncident.buildingName || '').trim();

            if (incidentBuildingID) {
              setIBuildingID?.(incidentBuildingID);
              localStorage.setItem('iBuildingID', incidentBuildingID);
              sessionStorage.setItem('iBuildingID', incidentBuildingID);
            }

            if (incidentBuildingName) {
              setBuildingName?.(incidentBuildingName);
              localStorage.setItem('buildingName', incidentBuildingName);
              sessionStorage.setItem('buildingName', incidentBuildingName);
            }
          }
        } catch (incidentError) {
          incidentLoadError = incidentError;
          console.error('Unable to load linked incident', incidentError);
        }
      }

      if (!targetIncident) {
        const defaultBuildingID = String(data.iBuildingID || campusIDs[0] || '').trim();
        const defaultBuildingName = String(data.buildingName || campusNames[0] || '').trim();

        if (defaultBuildingID) {
          setIBuildingID?.(defaultBuildingID);
          localStorage.setItem('iBuildingID', defaultBuildingID);
          sessionStorage.setItem('iBuildingID', defaultBuildingID);
        }

        if (defaultBuildingName) {
          setBuildingName?.(defaultBuildingName);
          localStorage.setItem('buildingName', defaultBuildingName);
          sessionStorage.setItem('buildingName', defaultBuildingName);
        }
      }

      setIAccountID?.(data.iAccountID);

      const { data: mfaStatus } = await axios.get('/authenticate-mfa-session', {
        params: linkedIncidentID
          ? { incidentID: linkedIncidentID }
          : undefined,
        headers: {
          iUserID: data.userID,
          iAccountID: data.iAccountID,
        },
        withCredentials: true,
      });

      const mfaVerified = mfaStatus?.mfaVerified === true;
      const mfaMode = mfaStatus?.mfaAuthenticationMode ||
        (mfaVerified ? 'trusted' : 'required');
      const emergencyIncidentID = String(
        mfaStatus?.emergencyIncidentID || mfaStatus?.incidentID || ''
      ).trim();

      if (mfaMode === 'emergency-incident' && emergencyIncidentID) {
        targetPath = `/activeincidentpage/${encodeURIComponent(emergencyIncidentID)}`;

        const emergencyBuildingID = String(
          mfaStatus?.iBuildingID || mfaStatus?.iCampusID || ''
        ).trim();
        const emergencyBuildingName = String(
          mfaStatus?.buildingName || ''
        ).trim();

        if (emergencyBuildingID) {
          setIBuildingID?.(emergencyBuildingID);
          localStorage.setItem('iBuildingID', emergencyBuildingID);
          sessionStorage.setItem('iBuildingID', emergencyBuildingID);
        }

        if (emergencyBuildingName) {
          setBuildingName?.(emergencyBuildingName);
          localStorage.setItem('buildingName', emergencyBuildingName);
          sessionStorage.setItem('buildingName', emergencyBuildingName);
        }
      }

      const userObj = {
        userRole: data.userRole,
        userID: data.userID,
        username: data.userName ?? username,
        userMobileNumber: data.mobileNumber,
        iAccountID: data.iAccountID,
        firstName: data.firstName,
        lastName: data.lastName,
        accountName: data.accountName,
        campusIDs,
        campusNames,
        isAuthenticated: true,
        isMfaAuthenticated: mfaVerified,
        mfaAuthenticationMode: mfaMode,
        mfaEmergencyIncidentID:
          mfaMode === 'emergency-incident' ? emergencyIncidentID : null,
      };

      setUser?.(userObj);
      sessionStorage.setItem('userInfo', JSON.stringify(userObj));

      if (!mfaVerified) {
        if (linkedIncidentID && !targetIncident && incidentLoadError) {
          targetPath = '/reportincidentpanel';
          toast.error(
            incidentLoadError.response?.data?.message ||
            'This incident link is no longer available for this user.'
          );
        }

        const oneTapChallengeID = String(
          data.oneTapMfaChallengeID || ''
        ).trim();
        const oneTapDeliveryMethods = Array.isArray(data.oneTapMfaDeliveryMethods)
          ? data.oneTapMfaDeliveryMethods
          : [];

        if (data.oneTapMfaSent === true && oneTapChallengeID) {
          sessionStorage.setItem(MFA_LINK_CHALLENGE_KEY, oneTapChallengeID);
          sessionStorage.setItem(
            MFA_LINK_DELIVERY_KEY,
            JSON.stringify(oneTapDeliveryMethods)
          );
        } else {
          sessionStorage.removeItem(MFA_LINK_CHALLENGE_KEY);
          sessionStorage.removeItem(MFA_LINK_DELIVERY_KEY);
        }

        rememberMfaReturnTo(targetPath, targetIncident?.incidentID || '');
        navigate('/mfaverification', {
          replace: true,
          state: {
            from: {
              pathname: targetPath,
              state: targetIncident ? { incident: targetIncident } : undefined,
            },
            oneTapChallengeID,
            oneTapDeliveryMethods,
          },
        });
        return;
      }

      sessionStorage.removeItem(MFA_LINK_CHALLENGE_KEY);
      sessionStorage.removeItem(MFA_LINK_DELIVERY_KEY);
      clearMfaReturnTo();

      if (emergencyIncidentID || targetIncident?.incidentID) {
        navigate(targetPath, {
          replace: true,
          state: targetIncident ? { incident: targetIncident } : undefined,
        });
        return;
      }

      const defaultBuildingID = String(data.iBuildingID || campusIDs[0] || '').trim();
      await routeToActiveOrPanel(data.iAccountID, defaultBuildingID);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Server error during login.');
    } finally {
      setBusy(false);
      setIsLoading?.(false);
    }
  };

  return (
    <Container fluid className={styles.loginPage}>
      <style>{`
        .incident-alert-password-input::-ms-reveal,
        .incident-alert-password-input::-ms-clear {
          display: none;
        }
      `}</style>

      <img
        src={IALERTTRANSImg}
        className={styles.logo}
        alt="IncidentAlert logo"
      />

      <div className={styles.content}>
        {!resetStep && (
          <Form
            className={styles.form}
            onSubmit={(e) => {
              e.preventDefault();
              handleLogin();
            }}
          >
            <Form.Group className="mb-2">
              <Form.Control
                size="sm"
                type="text"
                autoComplete="username"
                placeholder="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="text-center"
              />
            </Form.Group>

            <Form.Group className="mb-2">
              <PasswordControl
                id="incident-alert-login-password"
                name="password"
                autoComplete="current-password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                visible={showLoginPassword}
                onToggle={() =>
                  setShowLoginPassword((current) => !current)
                }
              />
            </Form.Group>

            <Button
              type="submit"
              variant="danger"
              size="sm"
              className="d-block mx-auto"
              style={{ width: '80%' }}
              disabled={busy}
            >
              {busy ? <Spinner animation="border" size="sm" /> : 'Login'}
            </Button>
          </Form>
        )}

        {resetStep === 1 && (
          <Form className={styles.form} onSubmit={handleSendResetCode}>
            <p className="text-center mb-2">
              Enter your username to receive a password reset code via email. Don't forget to check your spam folder and click "not spam".
            </p>

            <Form.Group className="mb-2">
              <Form.Control
                size="sm"
                type="text"
                autoComplete="username"
                placeholder="Username"
                value={resetForm.username}
                onChange={(e) =>
                  setResetForm((prev) => ({
                    ...prev,
                    username: e.target.value,
                  }))
                }
                className="text-center"
              />
            </Form.Group>

            <div className="d-flex justify-content-between gap-2">
              <Button
                type="button"
                variant="outline-secondary"
                size="sm"
                className="w-50"
                onClick={handleCancelReset}
                disabled={busy}
              >
                Cancel
              </Button>

              <Button
                type="submit"
                variant="danger"
                size="sm"
                className="w-50"
                disabled={busy}
              >
                {busy ? <Spinner animation="border" size="sm" /> : 'Send Code'}
              </Button>
            </div>
          </Form>
        )}

        {resetStep === 2 && (
          <Form className={styles.form} onSubmit={handleResetPassword}>
            <p className="text-center mb-2">
              Enter the reset code and choose a new password.
            </p>

            <Form.Group className="mb-2">
              <Form.Control
                size="sm"
                type="text"
                placeholder="Reset Code"
                value={resetForm.resetCode}
                onChange={(e) =>
                  setResetForm((prev) => ({
                    ...prev,
                    resetCode: e.target.value,
                  }))
                }
                className="text-center"
              />
            </Form.Group>

            <Form.Group className="mb-2">
              <PasswordControl
                id="incident-alert-new-password"
                name="newPassword"
                autoComplete="new-password"
                placeholder="New Password"
                value={resetForm.newPassword}
                onChange={(e) =>
                  setResetForm((prev) => ({
                    ...prev,
                    newPassword: e.target.value,
                  }))
                }
                visible={showNewPassword}
                onToggle={() =>
                  setShowNewPassword((current) => !current)
                }
              />
            </Form.Group>

            {resetForm.newPassword && (
              <>
                <ProgressBar
                  now={passwordStrengthPercent}
                  animated
                  variant={scoreVariant[score]}
                  className="mb-2"
                  label={`${Math.round(passwordStrengthPercent)}%`}
                />

                <ul
                  className="small mb-2"
                  style={{
                    listStyle: 'none',
                    paddingLeft: 0,
                    textAlign: 'left',
                  }}
                >
                  {RESET_RULES.map((rule) => (
                    <li key={rule.label}>
                      <span
                        style={{ marginRight: 6 }}
                        className={
                          rule.re.test(resetForm.newPassword)
                            ? 'text-success'
                            : 'text-muted'
                        }
                      >
                        {rule.re.test(resetForm.newPassword) ? '✔︎' : '✖︎'}
                      </span>
                      {rule.label}
                    </li>
                  ))}
                  <li>
                    <span
                      style={{ marginRight: 6 }}
                      className={score >= 3 ? 'text-success' : 'text-muted'}
                    >
                      {score >= 3 ? '✔︎' : '✖︎'}
                    </span>
                    reasonable entropy
                  </li>
                </ul>
              </>
            )}

            <Form.Group className="mb-2">
              <PasswordControl
                id="incident-alert-confirm-password"
                name="confirmPassword"
                autoComplete="new-password"
                placeholder="Confirm New Password"
                value={resetForm.confirmPassword}
                onChange={(e) =>
                  setResetForm((prev) => ({
                    ...prev,
                    confirmPassword: e.target.value,
                  }))
                }
                visible={showConfirmPassword}
                onToggle={() =>
                  setShowConfirmPassword((current) => !current)
                }
              />
            </Form.Group>

            <div className="d-flex justify-content-between gap-2">
              <Button
                type="button"
                variant="outline-secondary"
                size="sm"
                className="w-50"
                onClick={handleCancelReset}
                disabled={busy}
              >
                Cancel
              </Button>

              <Button
                type="submit"
                variant="danger"
                size="sm"
                className="w-50"
                disabled={
                  busy ||
                  !resetForm.resetCode.trim() ||
                  !strongPassword ||
                  resetForm.newPassword !== resetForm.confirmPassword
                }
              >
                {busy ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  'Update Password'
                )}
              </Button>
            </div>
          </Form>
        )}

        {!resetStep && (
          <Button
            type="button"
            variant="outline-danger"
            size="sm"
            className={styles.actionButton}
            onClick={handleStartReset}
            disabled={busy}
          >
            Reset Password
          </Button>
        )}

        {!!errorMsg && (
          <Alert variant="danger" className="mt-2 mb-0 py-2 text-center">
            {errorMsg}
          </Alert>
        )}

        {!!infoMsg && (
          <Alert variant="success" className="mt-2 mb-0 py-2 text-center">
            {infoMsg}
          </Alert>
        )}
      </div>

      <div className={styles.qrRow}>
        <Button
          type="button"
          variant="outline-danger"
          size="sm"
          className={styles.qrToggleButton}
          onClick={() => setShowQr((prev) => !prev)}
        >
          QR
        </Button>

        {installAvailable && (
          <Button
            type="button"
            variant="danger"
            size="sm"
            className={styles.installButton}
            onClick={handleInstall}
          >
            Install
          </Button>
        )}
      </div>

      {showQr && <img src={QRImg} alt="iAlert QR" className={styles.qr} />}

      <div className={styles.footer}>
        <h6 className={styles.footerText}>by SchoolShield</h6>
      </div>
    </Container>
  );
}